import React, {useState} from "react";
import {Box, Button, Chip, Divider, FormControl, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, Stack, TextField, Typography} from "@mui/material";
import {helpdeskTicketStatus, getHelpdeskTicketStatus} from "../../data/helpdesk/HelpdeskTicketStatus";
import {Division} from "../../data/Division";
import {UserSelector} from "../UserSelector";
import {UserResponse} from "../../data/UserResponse";

export interface FilterState {
    query: string
    division: string
    statuses: string[]
    assignee: string
}

export const emptyFilters: FilterState = {query: "", division: "", statuses: [], assignee: ""}

export function buildSearchUrl(filters: FilterState, page: number): string {
    const params = new URLSearchParams()
    params.set("page", String(page))
    if (filters.query) params.set("query", filters.query)
    if (filters.division) params.set("division", filters.division)
    if (filters.assignee) params.set("assignee", filters.assignee)
    filters.statuses.forEach(s => params.append("status", s.toUpperCase()))
    return `/api/helpdesk/search?${params.toString()}`
}

export function isFiltering(filters: FilterState): boolean {
    return filters.query !== "" || filters.division !== "" || filters.statuses.length > 0 || filters.assignee !== ""
}

interface HelpdeskFilterPanelProps {
    filters: FilterState
    divisions: Division[]
    onChange: (f: FilterState) => void
    onClear: () => void
}

export function HelpdeskFilterPanel({filters, divisions, onChange, onClear}: HelpdeskFilterPanelProps) {
    const [selectedAssignee, setSelectedAssignee] = useState<UserResponse | null>(null)

    const handleAssigneeChange = (user: UserResponse | null) => {
        setSelectedAssignee(user)
        onChange({...filters, assignee: user?.communityId ?? ""})
    }

    const handleClear = () => {
        setSelectedAssignee(null)
        onClear()
    }

    return <Stack spacing={2}>
        <Typography variant="subtitle2">Filters</Typography>
        <Divider/>
        <TextField
            label="Search title / ID / user"
            variant="outlined"
            size="small"
            fullWidth
            value={filters.query}
            onChange={e => onChange({...filters, query: e.target.value})}
        />
        <FormControl size="small" fullWidth>
            <InputLabel>Division</InputLabel>
            <Select
                value={filters.division}
                label="Division"
                onChange={(e: SelectChangeEvent) => onChange({...filters, division: e.target.value})}
            >
                <MenuItem value=""><em>Any</em></MenuItem>
                {divisions.map(d => <MenuItem key={d.id} value={d.identifier}>{d.displayName}</MenuItem>)}
            </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
                multiple
                value={filters.statuses}
                label="Status"
                input={<OutlinedInput label="Status"/>}
                onChange={(e: SelectChangeEvent<string[]>) =>
                    onChange({...filters, statuses: typeof e.target.value === "string" ? [e.target.value] : e.target.value})
                }
                renderValue={selected =>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        {(selected as string[]).map(v =>
                            <Chip key={v} label={getHelpdeskTicketStatus(v)?.displayName ?? v} size="small"/>
                        )}
                    </Box>
                }
            >
                {helpdeskTicketStatus.map(s =>
                    <MenuItem key={s.key} value={s.key}>{s.displayName}</MenuItem>
                )}
            </Select>
        </FormControl>
        <UserSelector
            user={selectedAssignee}
            onUserSelected={handleAssigneeChange}
            label="Assignee"
            size="small"
        />
        {isFiltering(filters) &&
            <Button size="small" color="warning" onClick={handleClear}>Clear filters</Button>
        }
    </Stack>
}
